/**
 * Toast host + the helpers every mutation should use.
 *
 * Before this, an approve/reject/refund/suspend either dropped its result into
 * a page-local `error` string or reported nothing at all — a successful refund
 * looked exactly like a click that missed. Every mutation now says what it did,
 * and destructive ones say it with an undo window where the API allows one.
 */
import { Toaster as SonnerToaster, toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { ApiError } from '@/lib/api';
import { use_theme_store, resolve_theme } from '@/lib/theme-store';

export function Toaster() {
  const choice = use_theme_store((s) => s.choice);
  return (
    <SonnerToaster
      position="bottom-right"
      // Sonner needs a resolved value; handing it 'system' works but then it
      // watches the OS itself and ignores an explicit in-app override.
      theme={resolve_theme(choice)}
      // Errors stay until dismissed (see `notify.error`); everything else
      // clears itself inside the 3-5s window.
      duration={4000}
      gap={10}
      offset={20}
      toastOptions={{
        classNames: {
          toast:
            'group !rounded-panel !border-0 !bg-card !text-card-foreground !shadow-overlay !font-body !text-sm',
          title: '!font-semibold',
          description: '!text-muted-foreground !text-data',
          actionButton:
            '!rounded-control !bg-primary !text-primary-foreground !text-xs !font-semibold',
          cancelButton: '!rounded-control !bg-muted !text-muted-foreground !text-xs !font-semibold',
          closeButton: '!rounded-full !border-border !bg-card !text-muted-foreground',
        },
      }}
      icons={{
        success: <CheckCircle2 className="h-4 w-4 text-success" />,
        error: <XCircle className="h-4 w-4 text-destructive" />,
        warning: <AlertTriangle className="h-4 w-4 text-warning" />,
        info: <Info className="h-4 w-4 text-info" />,
      }}
    />
  );
}

/** Pull the most useful sentence out of whatever a mutation rejected with. */
export function error_message(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}

type NotifyOptions = {
  description?: string;
  /** Adds an action button. Use for undo, or "View" deep-links. */
  action?: { label: string; on_click: () => void };
};

export const notify = {
  success(message: string, opts: NotifyOptions = {}) {
    return toast.success(message, {
      description: opts.description,
      action: opts.action
        ? { label: opts.action.label, onClick: opts.action.on_click }
        : undefined,
    });
  },

  /**
   * Failures do NOT auto-dismiss. A toast that vanishes after four seconds is
   * fine for "Saved"; for "Refund rejected by gateway" it destroys the only
   * copy of the reason before the admin has finished reading it. The trace id
   * rides along when the backend sent one, because that is what support asks
   * for first.
   */
  error(err_or_message: unknown, fallback?: string) {
    const message = typeof err_or_message === 'string'
      ? err_or_message
      : error_message(err_or_message, fallback);
    const trace = err_or_message instanceof ApiError ? err_or_message.trace_id : undefined;
    return toast.error(message, {
      duration: Infinity,
      closeButton: true,
      description: trace ? `Trace ${trace}` : undefined,
    });
  },

  info(message: string, opts: NotifyOptions = {}) {
    return toast.info(message, {
      description: opts.description,
      action: opts.action
        ? { label: opts.action.label, onClick: opts.action.on_click }
        : undefined,
    });
  },

  warning(message: string, opts: NotifyOptions = {}) {
    return toast.warning(message, { description: opts.description });
  },

  /** Pending → settled, for anything slow enough to need a spinner. */
  promise<T>(
    p: Promise<T>,
    msgs: { loading: string; success: string | ((v: T) => string); error?: string },
  ) {
    return toast.promise(p, {
      loading: msgs.loading,
      success: msgs.success,
      error: (e: unknown) => error_message(e, msgs.error),
    });
  },

  dismiss: toast.dismiss,
};
